import React from 'react';
import ProfileAvatar from './ProfileAvatar';
import EyeButton from './EyeButton';
import UnreadIndicator from './UnreadIndicator';
import { formatDateLocalized } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';

interface ProfileMessageCardProps {
  profile: {
    id?: string | null;
    firstName: string;
    avatarUrl?: string | null;
  };
  title: string;
  message: string;
  date: string;
  actionButton?: {
    label?: string;
    onClick?: () => void;
    href?: string;
    state?: Record<string, unknown> | null;
    loading?: boolean;
  };
  showUnreadIndicator?: boolean;
  highlightUnreadItem?: boolean;
  className?: string;
  variant?: 'default' | 'simple';
}

const ProfileMessageCard: React.FC<ProfileMessageCardProps> = ({
  profile,
  title,
  message,
  date,
  actionButton,
  showUnreadIndicator = false,
  highlightUnreadItem = false,
  className = '',
  variant = 'default',
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const formattedDate = formatDateLocalized(date, i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const variantStyles = {
    default: {
      outerClassName: 'rounded-3xl p-4 md:p-6',
      avatarClassName: 'md:w-16 md:h-16',
    },
    simple: {
      outerClassName: 'pb-4 border-b border-[#E1E9F0] last:border-b-0',
      avatarClassName: 'md:w-12 md:h-12',
    },
  };

  const handleOnClick = () => {
    if (actionButton?.loading) return;
    actionButton?.onClick?.();
    if (actionButton?.href) {
      navigate(actionButton.href, { state: actionButton.state });
    }
  };

  return (
    <div
      className={twMerge(
        'shadow-xl border-2 border-gray-300 bg-white cursor-pointer',
        variantStyles[variant].outerClassName,
        highlightUnreadItem && showUnreadIndicator && 'bg-[#E1E9F0]',
        className
      )}
      role="button"
      tabIndex={0}
      onClick={handleOnClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleOnClick()}
    >
      <div className="flex items-center gap-4">
        {/* LEFT BLOCK */}
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <ProfileAvatar
            profile_id={profile.id ?? undefined}
            first_name={profile.firstName}
            avatar_url={profile.avatarUrl}
            className={twMerge(
              'w-8 h-8 flex-shrink-0',
              highlightUnreadItem && showUnreadIndicator ? 'bg-primary text-white' : 'bg-[#E1E9F0]',
              variantStyles[variant].avatarClassName
            )}
          />

          <div className="flex min-w-0 flex-col">
            <div className="font-bold text-secondary">{title}</div>
            <div className="break-words text-secondary">{message}</div>
            <div className="mt-1 font-bold text-secondary">{formattedDate}</div>
          </div>
        </div>

        {/* RIGHT BLOCK */}
        {actionButton && (
          <div className="flex h-full flex-col items-center justify-between">
            {showUnreadIndicator && <UnreadIndicator />}
            <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
              <EyeButton
                onClick={handleOnClick}
                ariaLabel={actionButton.label}
                loading={actionButton.loading}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileMessageCard;
